import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from '@/pages/LandingPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import AdminDashboardPage from '@/pages/AdminDashboardPage';
import PrivacyPage from '@/pages/PrivacyPage';
import ContactPage from '@/pages/ContactPage';
import ImpressumPage from '@/pages/ImpressumPage';
import TermsPage from '@/pages/TermsPage';
import { api } from '@/lib/api';
import { I18nProvider } from '@/i18n/I18nProvider';
import '@/App.css';

/**
 * RequireAdmin probes the backend with the (HttpOnly) admin cookie to decide
 * whether the visitor is authenticated. The JWT itself is invisible to JS,
 * so we cannot synchronously inspect it — we instead call /api/admin/me
 * once and gate rendering on the response.
 */
function RequireAdmin({ children }) {
  const [state, setState] = useState('checking'); // 'checking' | 'authed' | 'unauthed'

  useEffect(() => {
    let cancelled = false;
    api
      .get('/admin/me')
      .then(() => {
        if (!cancelled) setState('authed');
      })
      .catch(() => {
        if (!cancelled) setState('unauthed');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'checking') {
    return (
      <div
        data-testid="admin-auth-check"
        className="min-h-screen flex items-center justify-center bg-background text-muted-foreground"
      >
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        <span className="text-sm">Checking session…</span>
      </div>
    );
  }
  if (state === 'unauthed') return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  useEffect(() => {
    // Force dark mode (per design guidelines)
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <div className="App">
      <I18nProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminDashboardPage />
                </RequireAdmin>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" theme="dark" />
      </I18nProvider>
    </div>
  );
}

export default App;
